import { CsRecipeInput, RecipeFlags, RecipeSchemaContext } from './types';

/** Empty for validation purposes — undefined, null, or whitespace-only. */
export function isEmptyValue(value: any): boolean {
    return value === undefined
        || value === null
        || (typeof value === 'string' && value.trim().length === 0);
}

/** Scene-building rows Product Photography hides while a preset is chosen. */
const HIDDEN_IN_PRESET = new Set(['backdrop', 'bgColor', 'lighting', 'shadow', 'depthOfField']);

/**
 * What Prompt mode answers in a sentence, so it must not also ask as a form.
 *
 * Wider than the preset list because a written brief settles the framing and the
 * camera too — a preset does not. Without this the Prompt tab rendered the whole
 * Custom option list beside the text box, which is how it came to look like a
 * second copy of Custom with nothing to type into.
 */
export const HIDDEN_IN_PROMPT = new Set([
    'preset', 'backdrop', 'backdropColor', 'bgColor', 'wallMaterial', 'wallMaterialCustom',
    'floorMaterial', 'floorMaterialCustom', 'lightingStyle', 'lightingStyleCustom',
    'lighting', 'shadow', 'depthOfField', 'framing', 'heroRotation', 'cameraHeight',
]);

/**
 * Everything Single Piece stopped asking when it became one page.
 *
 * The card is a brief and a picture now: the shot is described in words, so the
 * enumerations that used to assemble it — a mode, a preset, a backdrop, a
 * lighting, a shadow, a depth of field, a framing — have no rows. Three of them
 * were still DECLARED required, and a node the agent had filled in completely
 * reported "Needs a detail before it can run" while pointing at nothing the
 * vendor could see.
 *
 * `aspectRatio` is in the list for a subtler reason: it is shown, in the top
 * toolbar, but the toolbar renders `inputs.aspectRatio || the recipe default`,
 * so it reads as answered while the stored value is empty. A required field
 * that displays a value it has not got is the worst kind of block.
 */
const HIDDEN_ON_SINGLE_PIECE = new Set([
    'backdrop', 'bgColor', 'lighting', 'shadow', 'depthOfField',
    'framing', 'heroRotation', 'cameraHeight', 'roomType', 'style', 'snapshotUrl',
    'aspectRatio',
]);

/**
 * Recipes that produce a SCENE, as opposed to editing a picture that exists.
 *
 * These are the ones the 3D studio is for: the camera, and therefore the depth
 * and the object mask, come from a capture rather than from an adjective.
 */
export const SCENE_RECIPES = new Set([
    'lifestyle-scene',
    'lifestyle-room',
    'product-closeup',
    'from-reference',
]);

/** style-variation mode rows own each other's hidden sets. */
const SV_CAMERA_KEYS = new Set(['azimuth', 'elevation', 'distance']);
const SV_PROMPT_KEYS = new Set(['variationStrength', 'stylePrompt']);
const SV_CLOSEUP_KEYS = new Set(['closeUpZoom']);

/**
 * Should a declared-required input be skipped because it isn't on screen?
 *
 * These rules mirror the render loop exactly — a field the vendor cannot see
 * must never block Generate. Kept in one place precisely so the two can't
 * drift apart.
 */
export function isHiddenForValidation(
    input: CsRecipeInput,
    ctx: RecipeSchemaContext,
    flags: RecipeFlags,
): boolean {
    const { recipe, inputs, productVisualizationType: vt } = ctx;
    const { hasMode, modeValue } = flags;
    const key = input.key;

    /**
     * `mode` is rendered as TABS, never as a row.
     *
     * The render loop drops it whenever a recipe has them, so a node that has
     * not stored one shows the default tab selected and offers the vendor
     * nothing to fill — while the required-field check counts it as missing.
     * True of every tabbed recipe, so the rule belongs here rather than beside
     * one of them.
     */
    if (key === 'mode' && hasMode) return true;

    // Rows gated on a dependent field's value.
    if (key === 'heroRotation' && inputs.framing !== 'angled-hero') return true;
    if (key === 'environmentCustom' && inputs.environment !== 'custom') return true;
    if (key === 'styleCustom' && inputs.style !== 'custom') return true;
    if (key === 'roomTypeCustom') return true; // rendered inline in the card

    if (recipe.slug === 'product-closeup' && hasMode && modeValue === 'preset' && HIDDEN_IN_PRESET.has(key)) {
        return true;
    }

    // The brief IS the answer in Prompt mode; the option rows are the other way
    // of answering the same question and showing both offers the model two.
    if (recipe.slug === 'product-closeup' && hasMode && modeValue === 'prompt' && HIDDEN_IN_PROMPT.has(key)) {
        return true;
    }

    // Single Piece: one brief, one optional picture. Mirrors the card's own
    // hide list — the two are kept together on purpose, and this is what
    // happens when they drift.
    if (flags.isSurfaceSingle && HIDDEN_ON_SINGLE_PIECE.has(key)) return true;

    // Lifestyle furniture: the scene fields aren't required in Prompt mode
    // (the prompt drives the scene) or Use AI mode (the agent picks them).
    // Surface only: the material is installed and shot at the chosen angle,
    // and no room is built. Environment and Style describe a room's setting
    // and decor, so with no room they are questions about nothing — asked,
    // they read as settings that quietly do not apply.
    if (recipe.slug === 'product-closeup' && vt === 'surface' && flags.appliedProductOnly
        && ['roomType', 'roomTypeCustom', 'style', 'styleCustom'].includes(key)) {
        return true;
    }

    // Keep-the-photo: the picture already IS the room, the style, the light and
    // the shape of the frame. Asked anyway, these read as settings that quietly
    // do not apply — and one of them, aspect ratio, would look like it could
    // change an output that keeps the reference's own proportions.
    // Same-structure answers them from the reference too: its scene prompt is
    // read off the picture, and its output is sized to the picture's own depth.
    if (recipe.slug === 'from-reference' && (flags.fromRefExact || flags.fromRefSameStructure)
        && ['style', 'styleCustom', 'roomType', 'roomTypeCustom', 'lighting', 'aspectRatio']
            .includes(key)) {
        return true;
    }

    if (recipe.slug === 'lifestyle-scene' && vt === 'model3d' && hasMode) {
        if (['roomType', 'style', 'lighting'].includes(key) && (modeValue === 'prompt' || modeValue === 'ai')) {
            return true;
        }
    }

    // Image to Video · Prompt: the brief replaces the preset camera rows.
    if (recipe.slug === 'image-to-video' && modeValue === 'prompt'
        && ['cameraMove', 'intensity', 'lightingTransition'].includes(key)) {
        return true;
    }

    if (recipe.slug === 'style-variation') {
        if (modeValue === 'prompt' && (SV_CAMERA_KEYS.has(key) || SV_CLOSEUP_KEYS.has(key))) return true;
        if (modeValue === 'change-angle' && (SV_PROMPT_KEYS.has(key) || SV_CLOSEUP_KEYS.has(key))) return true;
        if (modeValue === '3d-rotation' && (SV_CAMERA_KEYS.has(key) || SV_PROMPT_KEYS.has(key) || SV_CLOSEUP_KEYS.has(key))) return true;
        // Close-up drives framing from the crop widget, so the camera, prompt,
        // zoom and aspect rows are all off-screen.
        if (modeValue === 'close-up'
            && (SV_CAMERA_KEYS.has(key) || SV_PROMPT_KEYS.has(key) || SV_CLOSEUP_KEYS.has(key) || key === 'aspectRatio')) {
            return true;
        }
        if (SV_CLOSEUP_KEYS.has(key)) return true;
    }

    return false;
}

/**
 * Every required-but-empty visible input, in declaration order.
 *
 * Covers four families of rule: declared-required fields, mode-specific
 * demands (a preset must be picked, a prompt must be written), the paired
 * free-text that a `custom` selection requires, and the uploads/captures a
 * recipe cannot run without.
 */
export function resolveMissingInputs(
    ctx: RecipeSchemaContext,
    flags: RecipeFlags,
    visibleInputs: CsRecipeInput[],
): string[] {
    const { recipe, inputs, productVisualizationType: vt } = ctx;
    const { hasMode, modeValue, surfaceMode, isSurfaceApplied, roomSceneStudio,
        fromRefStudio, fromRefExact, fromRefSameStructure, appliedProductOnly, use3dOn } = flags;
    const missing: string[] = [];

    // 1. Declared-required inputs that are visible and empty.
    for (const input of visibleInputs) {
        if (!input.required) continue;
        if (isHiddenForValidation(input, ctx, flags)) continue;
        if (isEmptyValue(inputs[input.key])) missing.push(input.key);
    }

    // 2. Product Photography preset mode demands a preset pick.
    // A detail shot is the shot itself; no preset is involved.
    if (recipe.slug === 'product-closeup' && hasMode && modeValue === 'preset' && isEmptyValue(inputs.preset)
        && inputs.shot !== 'detail') {
        missing.push('preset');
    }

    // 3. A `custom` selection requires its paired free text.
    const missingCustom = (key: string, customKey: string) =>
        inputs[key] === 'custom' && isEmptyValue(inputs[customKey]);

    // `environment` belongs to furniture's Lifestyle Scene. Room Scene asks for
    // `roomType` instead — it no longer has a Lifestyle mode that used
    // `environment`, so a stale value on the hidden field cannot block Generate.
    const envInPlay = (recipe.slug === 'lifestyle-scene' && vt === 'surface');
    const roomTypeInPlay = (recipe.slug === 'lifestyle-scene' && vt === 'model3d')
        || (isSurfaceApplied && roomSceneStudio && modeValue === 'options');

    if (envInPlay && missingCustom('environment', 'environmentCustom')) missing.push('environmentCustom');
    if (roomTypeInPlay && missingCustom('roomType', 'roomTypeCustom')) missing.push('roomTypeCustom');
    // Style is gated for the same reason environment and roomType are: a
    // 'custom' left on a field that is no longer shown must not hold Generate
    // hostage. Surface-only hides Style entirely, so a stale value from a
    // previous full-room setup would have blocked a shot that never asks.
    const styleInPlay = !isHiddenForValidation(
        { key: 'style', label: 'Style', type: 'select' } as CsRecipeInput, ctx, flags,
    );
    if (styleInPlay && missingCustom('style', 'styleCustom')) missing.push('styleCustom');

    // 4. Prompt modes need a scene description.
    if (recipe.slug === 'lifestyle-scene' && vt === 'model3d' && hasMode && modeValue === 'prompt'
        && isEmptyValue(inputs.sceneDetails)) {
        missing.push('sceneDetails');
    }
    // Image to Video · Prompt: the description IS the video.
    if (recipe.slug === 'image-to-video' && hasMode && modeValue === 'prompt'
        && isEmptyValue(inputs.sceneDetails)) {
        missing.push('sceneDetails');
    }
    // Surface Applied · Prompt too — unless "Product only", which has no scene.
    if (recipe.slug === 'product-closeup' && vt === 'surface' && surfaceMode === 'applied'
        && hasMode && modeValue === 'prompt' && !appliedProductOnly
        && isEmptyValue(inputs.sceneDetails)) {
        missing.push('sceneDetails');
    }

    // 5. Captures and uploads the pipeline cannot run without.
    // Room Scene only: Single Piece has no studio, so a stale `use3dCamera`
    // must not make it wait for a frame. (Third statement of the same rule in
    // this function — see the two below. They should be one.)
    if (recipe.slug === 'product-closeup' && vt === 'surface' && use3dOn && inputs.shot !== 'detail'
        && !flags.isSurfaceSingle && isEmptyValue(inputs.snapshotUrl)) {
        missing.push('snapshotUrl');
    }
    // A scene recipe with the studio switched on cannot run on words alone.
    // The capture is what carries the camera, and with it the depth and the
    // object mask the render is built from — described in a prompt, the angle
    // drifts and a close-up falls apart.
    // ...except where the route poses nothing. An exact copy replaces the
    // surface inside the vendor's own photograph, so there is no capture to
    // wait for, and a node carrying `use3dCamera` from before that choice
    // existed would otherwise be blocked on a requirement it cannot satisfy.
    // ...and except Single Piece, which photographs the product's own picture
    // and has no studio to open. A plan that sets `use3dCamera` there — and
    // they do, because a louder rule elsewhere says the studio is mandatory —
    // leaves a node waiting for a frame nobody can capture. That is the same
    // deadlock the exact-copy route had, arriving through a different door.
    // ...and except a detail shot, made from the product's own photograph.
    // (A detail shot posed in the 3D studio does need its frame.)
    const detailShot = recipe.slug === 'product-closeup' && inputs.shot === 'detail' && inputs.detailCamera !== 'studio';
    if (SCENE_RECIPES.has(recipe.slug) && use3dOn && !detailShot
        && !fromRefExact && !fromRefSameStructure && !flags.isSurfaceSingle
        && isEmptyValue(inputs.snapshotUrl)) {
        missing.push('snapshotUrl');
    }
    // Single Piece asks one question, so that question is the requirement: what
    // should the picture BE? Either written as a brief or shown as a reference
    // — both answer it, and neither is needed twice. Without one of them the
    // card would generate whatever it liked from the product photo alone,
    // which is not a shot anybody asked for.
    if (flags.isSurfaceSingle && modeValue !== 'preset' && !detailShot
        && isEmptyValue(inputs.sceneDetails)
        && isEmptyValue(inputs.inspirationUrl)
        && isEmptyValue(inputs.styleReferenceUrl)) {
        missing.push('sceneDetails');
    }

    if (recipe.slug === 'from-reference') {
        // The reference image is the whole premise — without it the model has
        // nothing to match against.
        if (isEmptyValue(inputs.styleReferenceUrl)) missing.push('styleReferenceUrl');
        if (fromRefStudio && isEmptyValue(inputs.snapshotUrl)) missing.push('snapshotUrl');
    }

    if (recipe.slug === 'place-in-room') {
        // The room is the image this was chained off. What must be set here is
        // the piece and its placement — the composite IS the job; the render
        // only makes it photoreal.
        if (isEmptyValue(inputs.placeProduct)) missing.push('placeProduct');
        if (isEmptyValue(inputs.compositeUrl)) missing.push('compositeUrl');
    }

    // Deduped, because the rules above overlap: a surface Room Scene with the
    // studio on satisfies both the product-closeup clause and the SCENE_RECIPES
    // one, and reported `snapshotUrl` twice — so the card counted two missing
    // details where there is one, and would have named it twice to the vendor.
    // The overlap itself is the real defect; until those clauses are made one,
    // this stops it reaching the screen.
    return [...new Set(missing)];
}
